import { useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { CAR_SEAT_AUTO_SELECT_MAX_AGE, MIN_CAR_SEAT_AGE } from '@rideiq/riders/constants/riderServices';
import { differenceInYears } from 'date-fns';

import { useLiveServices } from '../../../hooks/useLiveServices';
import type { RiderFormValues } from '../../../types';

export const useCarSeatAutoCheck = () => {
  const { control, getValues, setValue } = useFormContext<RiderFormValues>();
  const dateOfBirth = useWatch({ control, name: 'dateOfBirth' });
  const liveServicesState = useLiveServices();
  const prevDobRef = useRef(dateOfBirth);

  useEffect(() => {
    const prevDob = prevDobRef.current;

    const isSameDate =
      (prevDob == null && dateOfBirth == null) ||
      (prevDob != null && dateOfBirth != null && new Date(prevDob).getTime() === new Date(dateOfBirth).getTime());

    if (isSameDate || !dateOfBirth) {
      prevDobRef.current = dateOfBirth;

      return;
    }

    // Don't advance the ref when services aren't ready — retry once they load.
    if (liveServicesState.status !== 'ready') {
      return;
    }

    prevDobRef.current = dateOfBirth;

    const age = differenceInYears(Date.now(), new Date(dateOfBirth));
    const isCSAvailable = liveServicesState.services.some((s) => s.code === 'CS' && s.available);
    const currentServices = getValues('services') ?? [];
    const hasCS = currentServices.includes('CS');
    const isInAutoSelectRange = age >= MIN_CAR_SEAT_AGE && age <= CAR_SEAT_AUTO_SELECT_MAX_AGE;

    if (isCSAvailable && isInAutoSelectRange && !hasCS) {
      setValue('services', [...currentServices, 'CS'], { shouldDirty: true });
    } else if (age < MIN_CAR_SEAT_AGE && hasCS) {
      setValue(
        'services',
        currentServices.filter((code) => code !== 'CS'),
        { shouldDirty: true }
      );
    }
  }, [dateOfBirth, liveServicesState, getValues, setValue]);
};
